import React, { useMemo, useState } from "react";
import {
	CCAvatar,
	CCButton,
	CCField,
	CCSpinner,
	useToast,
} from "@pymblecitizencoders/cc-ui";
import { TypedLottieSpinner } from "pymble-ui/components";
import { TriangleAlert, UserPlus, X } from "lucide-react";
import { usePublishRoles } from "../hooks/useEventPublish";
import { useFederatedSearch } from "../hooks/useFederatedSearch";
import { useProfileImages } from "../hooks/useProfileImages";
import { personName } from "../utils/personName";
import "../components/eventManager/eventManager.css";

/**
 * Who signs off events — the Roles tab of approval administration.
 *
 * The publishing roles and the people who hold them.
 *
 * A role is a job, not a person and not a group. It exists whether or not
 * anyone holds it, which is why a role with no live holder is called out here:
 * that is the condition that stops events being submitted at all, and it is
 * invisible until someone tries.
 *
 * ⚠️ PII. Names and search results only, held in memory for the life of the
 * page, never logged.
 */

/** Ending a tenure is a real decision, so it asks first. */
const CONFIRM_END = "End this person's tenure in this role?";

export const RolesAdmin: React.FC = () => {
	const { imageUrl } = useProfileImages();
	const toast = useToast();
	const {
		roles,
		rolesLoading,
		rolesError,
		addHolder,
		endHolder,
		setDelegate,
		rolesSaving,
	} = usePublishRoles();

	const [openRole, setOpenRole] = useState<string | null>(null);
	/* Staff only: a role is a job someone does, so the search that fills it
	   should not offer students or carers. */
	const search = useFederatedSearch(useMemo(() => ["staff" as const], []));

	const add = async (roleId: string, contactId: string) => {
		try {
			await addHolder({ roleId, contactId });
			search.setTerm("");
			toast.show({ message: "Added.", severity: "success", duration: 2500 });
		} catch {
			toast.show({
				message: "Could not add that person to the role.",
				severity: "danger",
				duration: 5000,
			});
		}
	};

	const end = async (roleId: string, holderId: string) => {
		if (!window.confirm(CONFIRM_END)) return;
		try {
			await endHolder({ roleId, holderId });
			toast.show({
				message: "Tenure ended.",
				severity: "success",
				duration: 2500,
			});
		} catch {
			toast.show({
				message: "Could not end that tenure.",
				severity: "danger",
				duration: 5000,
			});
		}
	};

	const clearDelegate = async (roleId: string, holderId: string) => {
		try {
			await setDelegate({ roleId, holderId, delegateTo: null });
			toast.show({
				message: "Delegate cleared.",
				severity: "success",
				duration: 2500,
			});
		} catch {
			toast.show({
				message: "Could not clear the delegate.",
				severity: "danger",
				duration: 5000,
			});
		}
	};

	if (rolesLoading) {
		return (
			<div className="em-approve-loading">
				<TypedLottieSpinner
					type="Searching"
					size={180}
					label="Loading roles..."
				/>
			</div>
		);
	}

	return (
		<>
			{rolesError ? (
				<div className="em-picker-empty">
					The roles could not be loaded. You may not have permission to manage
					them.
				</div>
			) : roles.length === 0 ? (
				<div className="em-picker-empty">
					No roles have been set up. Ask for the &ldquo;EventPublishRoles&rdquo;
					dictionary to be populated.
				</div>
			) : (
				<div className="em-roles">
					{roles.map((role) => {
						const live = role.Holders.filter((h) => h.Live);
						const open = openRole === role.Code;
						return (
							<div key={role.Code} className="em-role">
								<div className="em-role__head">
									<div>
										<span className="em-role__name">
											{role.Name || role.Code}
										</span>
										{role.Description && (
											<span className="em-muted">{role.Description}</span>
										)}
									</div>
									{live.length === 0 && !role.Derived ? (
										<span className="em-role__warn">
											<TriangleAlert size={14} aria-hidden="true" /> Nobody
											holds this
										</span>
									) : null}
								</div>

								{live.length > 0 && (
									<ul className="em-role__holders">
										{live.map((h) => (
											<li key={h._id}>
												<CCAvatar
													src={imageUrl(h.ContactId)}
													name={personName(h.Name) || h.ContactId}
													size={24}
													decorative
												/>
												<span className="em-role__holder-name">
													{personName(h.Name) || h.ContactId}
													{/* Their job, under their name. Two people called
															    Sandejas are told apart by "Web Developer" and
															    not by an id, and the person filling a role
															    needs to be recognisable as the right one. */}
													{h.Title && (
														<span className="em-role__holder-title">
															{h.Title}
														</span>
													)}
												</span>
												{h.DelegateName && (
													<span className="em-muted">
														delegated to {h.DelegateName}
													</span>
												)}
												{/* A derived role's people come from a permission, so
												    there is no tenure to end or delegate to clear here. */}
												{!role.Derived && h.DelegateName && (
													<CCButton
														variant="outline"
														size="sm"
														disabled={rolesSaving}
														onClick={() => clearDelegate(role.Code, h._id)}
													>
														Clear delegate
													</CCButton>
												)}
												{!role.Derived && (
													<CCButton
														variant="outline"
														size="sm"
														disabled={rolesSaving}
														onClick={() => end(role.Code, h._id)}
													>
														<X size={14} aria-hidden="true" />
														<span className="em-sr-only">End tenure</span>
													</CCButton>
												)}
											</li>
										))}
									</ul>
								)}

								{role.Derived ? null : !open ? (
									<CCButton
										variant="outline"
										size="sm"
										onClick={() => {
											search.setTerm("");
											setOpenRole(role.Code);
										}}
									>
										<UserPlus size={15} aria-hidden="true" /> Add someone
									</CCButton>
								) : (
									<div className="em-role__add">
										<CCField
											id={`em-role-search-${role.Code}`}
											label="Search staff"
											type="search"
											size="sm"
											fullWidth
											autoFocus
											value={search.term}
											onChange={(e) => search.setTerm(e.target.value)}
										/>
										{search.searching ? (
											<CCSpinner size="sm" label="Searching..." />
										) : !search.longEnough ? (
											<p className="em-muted">
												Type at least {search.minQueryLength} characters.
											</p>
										) : search.hits.length === 0 ? (
											<p className="em-muted">No staff found.</p>
										) : (
											<ul className="em-role__hits">
												{search.hits.map((hit) => (
													<li key={hit.id}>
														<button
															type="button"
															disabled={rolesSaving}
															onClick={() => add(role.Code, hit.id)}
														>
															<CCAvatar
																src={imageUrl(hit.id)}
																name={personName(hit.title) || hit.title}
																size={24}
																decorative
															/>
															<span className="em-role__holder-name">
																{hit.title}
																{hit.subtitle && (
																	<span className="em-role__holder-title">
																		{hit.subtitle}
																	</span>
																)}
															</span>
														</button>
													</li>
												))}
											</ul>
										)}
										<CCButton
											variant="outline"
											size="sm"
											onClick={() => setOpenRole(null)}
										>
											Done
										</CCButton>
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}
		</>
	);
};
